import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../hooks/useToast';
import { useSensitiveDataBlur } from '../hooks/useSensitiveDataBlur';
import { useUserStore } from '../stores/useUserStore';
import { SensitiveFieldConfig } from '../types/sensitiveData';

// shadcn/ui components
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Lucide React icons
import { Shield, Eye, EyeOff, RotateCcw, Save, Info } from 'lucide-react';

const SensitiveDataSettings: React.FC = () => {
  const { translate } = useTranslation();
  const { success, error: showError } = useToast();

  // Debug: Log translation test
  if (process.env.NODE_ENV === 'development') {
    const testTranslation = translate(
      'pos.settings.sensitiveData.title',
      'Sensitive Data Protection'
    );
    console.log('🔍 Sensitive Data Translation Test:', {
      key: 'pos.settings.sensitiveData.title',
      result: testTranslation,
      isFallback: testTranslation === 'Sensitive Data Protection',
    });
  }
  const { user } = useUserStore();
  const {
    config,
    updateConfig,
    updateField,
    resetToDefault,
    activateBlur,
    deactivateBlur,
  } = useSensitiveDataBlur();

  const [previewMode, setPreviewMode] = useState(false);
  const previewWindowRef = useRef<Window | null>(null);
  const checkClosedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isInPreviewTab, setIsInPreviewTab] = useState(false);

  // Check if current tab is in preview mode
  useEffect(() => {
    const checkPreviewTab = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasPreviewParam = urlParams.get('preview') === 'true';
      const previewMode = sessionStorage.getItem('sensitive-data-preview-mode');
      setIsInPreviewTab(previewMode === 'true' || hasPreviewParam);
    };

    checkPreviewTab();
    const interval = setInterval(checkPreviewTab, 500);
    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (checkClosedIntervalRef.current) {
        clearInterval(checkClosedIntervalRef.current);
      }
      // Don't close the window on unmount - let user manage it
    };
  }, []);

  // Check if user has permission to access sensitive data settings
  const hasPermission = () => {
    if (!user) return false;
    const userRoles = user.roles || [];
    return (
      userRoles.includes('admin') ||
      userRoles.includes('orgAdmin') ||
      userRoles.includes('sysAdmin') ||
      userRoles.includes('storeManager')
    );
  };

  const canAccess = hasPermission();

  const handleToggleEnabled = (checked: boolean) => {
    updateConfig({ enabled: checked });
    if (checked) {
      success(
        translate(
          'pos.settings.sensitiveData.enabled',
          'Sensitive data blurring enabled'
        )
      );
    } else {
      success(
        translate(
          'pos.settings.sensitiveData.disabled',
          'Sensitive data blurring disabled'
        )
      );
      if (previewMode) {
        setPreviewMode(false);
        deactivateBlur();
      }
    }
  };

  const handleToggleField = (fieldId: string, enabled: boolean) => {
    updateField(fieldId, { enabled });
    success(
      enabled
        ? translate(
            'pos.settings.sensitiveData.fieldEnabled',
            'Enabled blurring for field'
          )
        : translate(
            'pos.settings.sensitiveData.fieldDisabled',
            'Disabled blurring for field'
          )
    );
  };

  const handleBlurIntensityChange = (value: string) => {
    const intensity = parseInt(value, 10);
    updateConfig({ blurIntensity: intensity });
  };

  const handlePreview = () => {
    if (previewMode) {
      // Stop preview mode and close the preview tab
      setPreviewMode(false);

      // Close the preview window if it's still open
      if (previewWindowRef.current && !previewWindowRef.current.closed) {
        try {
          previewWindowRef.current.close();
          success(
            translate(
              'pos.settings.sensitiveData.previewStoppedAndClosed',
              'Preview stopped and tab closed'
            )
          );
        } catch (error) {
          // If we can't close it (e.g., user already closed it), just notify
          success(
            translate(
              'pos.settings.sensitiveData.previewStopped',
              'Preview stopped'
            )
          );
        }
      } else {
        success(
          translate(
            'pos.settings.sensitiveData.previewStopped',
            'Preview stopped'
          )
        );
      }

      previewWindowRef.current = null;

      // Cleanup interval
      if (checkClosedIntervalRef.current) {
        clearInterval(checkClosedIntervalRef.current);
        checkClosedIntervalRef.current = null;
      }
    } else {
      // Enable preview mode and open new tab
      setPreviewMode(true);

      // Ensure blurring is enabled for preview
      const wasEnabled = config.enabled;
      if (!wasEnabled) {
        updateConfig({ enabled: true });
      }

      // Open new tab/window with preview parameter in URL
      // This ensures only the new tab activates preview mode
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('preview', 'true');
      const newWindow = window.open(currentUrl.toString(), '_blank');

      if (newWindow) {
        // Store reference to the preview window
        previewWindowRef.current = newWindow;
        success(
          translate(
            'pos.settings.sensitiveData.previewWindowOpened',
            'Preview window opened - blurring is active in the new tab'
          )
        );

        // Check if window was closed by user
        if (checkClosedIntervalRef.current) {
          clearInterval(checkClosedIntervalRef.current);
        }

        checkClosedIntervalRef.current = setInterval(() => {
          if (newWindow.closed) {
            setPreviewMode(false);
            previewWindowRef.current = null;
            if (checkClosedIntervalRef.current) {
              clearInterval(checkClosedIntervalRef.current);
              checkClosedIntervalRef.current = null;
            }
          }
        }, 500);

        // Cleanup interval after 5 minutes (preview shouldn't last that long anyway)
        setTimeout(() => {
          if (checkClosedIntervalRef.current) {
            clearInterval(checkClosedIntervalRef.current);
            checkClosedIntervalRef.current = null;
          }
        }, 5 * 60 * 1000);
      } else {
        // Popup blocked, show error
        showError(
          translate(
            'pos.settings.sensitiveData.allowPopups',
            'Please allow popups to open preview window'
          )
        );
        setPreviewMode(false);
        if (!wasEnabled) {
          updateConfig({ enabled: false });
        }
      }
    }
  };

  const handleReset = () => {
    resetToDefault();
    success(
      translate(
        'pos.settings.sensitiveData.resetToDefault',
        'Reset to default configuration'
      )
    );
  };

  const handleSave = () => {
    // Configuration is automatically saved to localStorage
    success(
      translate(
        'pos.settings.sensitiveData.settingsSaved',
        'Settings saved successfully'
      )
    );
  };

  // Use useMemo to ensure categoryLabels are recalculated when translate function changes
  // Must be called before any early returns to follow Rules of Hooks
  const categoryLabels: Record<string, string> = useMemo(
    () => ({
      personal: translate(
        'pos.settings.sensitiveData.categories.personal',
        'Personal Information'
      ),
      financial: translate(
        'pos.settings.sensitiveData.categories.financial',
        'Financial Information'
      ),
      contact: translate(
        'pos.settings.sensitiveData.categories.contact',
        'Contact Information'
      ),
      identification: translate(
        'pos.settings.sensitiveData.categories.identification',
        'Identification'
      ),
      other: translate('pos.settings.sensitiveData.categories.other', 'Other'),
    }),
    [translate]
  );

  if (!canAccess) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {translate(
            'pos.settings.sensitiveData.noPermission',
            'You do not have permission to access sensitive data settings. Only administrators and store managers can access this page.'
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Group fields by category
  const fieldsByCategory = config.fields.reduce(
    (
      acc: Record<string, SensitiveFieldConfig[]>,
      field: SensitiveFieldConfig
    ) => {
      if (!acc[field.category]) {
        acc[field.category] = [];
      }
      acc[field.category].push(field);
      return acc;
    },
    {} as Record<string, SensitiveFieldConfig[]>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            {translate(
              'pos.settings.sensitiveData.title',
              'Sensitive Data Protection'
            )}
          </h2>
          <p className="text-muted-foreground">
            {translate(
              'pos.settings.sensitiveData.description',
              'Configure which fields should be blurred when taking screenshots, reporting bugs, or sending screenshots to the AI assistant'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {!previewMode && !isInPreviewTab && (
            <Button variant="outline" onClick={handlePreview}>
              <Eye className="h-4 w-4 mr-2" />
              {translate('pos.settings.sensitiveData.preview', 'Preview')}
            </Button>
          )}
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            {translate('pos.settings.sensitiveData.reset', 'Reset')}
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {translate('pos.settings.sensitiveData.save', 'Save')}
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          {translate(
            'pos.settings.sensitiveData.info',
            'Blurring is automatically activated when you take a screenshot, report a bug, or send a screenshot to the AI assistant. Use the preview button to test your configuration.'
          )}
        </AlertDescription>
      </Alert>

      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate(
              'pos.settings.sensitiveData.globalSettings',
              'Global Settings'
            )}
          </CardTitle>
          <CardDescription>
            {translate(
              'pos.settings.sensitiveData.globalSettingsDescription',
              'Enable or disable sensitive data blurring globally'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-blurring">
                {translate(
                  'pos.settings.sensitiveData.enableBlurring',
                  'Enable Sensitive Data Blurring'
                )}
              </Label>
              <p className="text-sm text-muted-foreground">
                {translate(
                  'pos.settings.sensitiveData.enableBlurringDescription',
                  'When enabled, sensitive data will be automatically blurred during screenshots, bug reports, and AI assistant interactions'
                )}
              </p>
            </div>
            <Switch
              id="enable-blurring"
              checked={config.enabled}
              onCheckedChange={handleToggleEnabled}
            />
          </div>

          {config.enabled && (
            <div className="space-y-2">
              <Label htmlFor="blur-intensity">
                {translate(
                  'pos.settings.sensitiveData.blurIntensity',
                  'Blur Intensity'
                )}
              </Label>
              <Select
                value={config.blurIntensity.toString()}
                onValueChange={handleBlurIntensityChange}
              >
                <SelectTrigger id="blur-intensity" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                    <SelectItem key={value} value={value.toString()}>
                      {value}{' '}
                      {value === 10
                        ? `(${translate(
                            'pos.settings.sensitiveData.maximum',
                            'Maximum'
                          )})`
                        : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {translate(
                  'pos.settings.sensitiveData.blurIntensityDescription',
                  'Higher values create stronger blur. Recommended: 8'
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Configuration */}
      {config.enabled && (
        <Card>
          <CardHeader>
            <CardTitle>
              {translate(
                'pos.settings.sensitiveData.fieldConfiguration',
                'Field Configuration'
              )}
            </CardTitle>
            <CardDescription>
              {translate(
                'pos.settings.sensitiveData.fieldConfigurationDescription',
                'Configure which fields should be blurred. Fields are automatically detected based on CSS selectors and data attributes.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.entries(fieldsByCategory).map(([category, fields]) => {
                const typedFields = fields as SensitiveFieldConfig[];
                return (
                  <div key={category} className="space-y-2">
                    <h3 className="font-semibold text-lg">
                      {categoryLabels[category] || category}
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            {translate(
                              'pos.settings.sensitiveData.table.field',
                              'Field'
                            )}
                          </TableHead>
                          <TableHead>
                            {translate(
                              'pos.settings.sensitiveData.table.description',
                              'Description'
                            )}
                          </TableHead>
                          <TableHead>
                            {translate(
                              'pos.settings.sensitiveData.table.selector',
                              'Selector'
                            )}
                          </TableHead>
                          <TableHead className="text-right">
                            {translate(
                              'pos.settings.sensitiveData.table.enabled',
                              'Enabled'
                            )}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {typedFields.map((field) => (
                          <TableRow key={field.id}>
                            <TableCell className="font-medium">
                              {field.label}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {field.description || '-'}
                            </TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground max-w-xs truncate">
                              {field.selector || field.dataAttribute || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Switch
                                checked={field.enabled}
                                onCheckedChange={(checked) =>
                                  handleToggleField(field.id, checked)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SensitiveDataSettings;
